import { FilterGroup } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog } from '@progress/kendo-react-dialogs';
import { TextArea, TextBox } from '@progress/kendo-react-inputs';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AddOutline, SearchOutline, UserGroupOutline, UserOutline } from '../../../../../../icons/icons';
import { FilterGroupType } from '../../../../../../types/filter-group';
import { DropdownList, DropdownListWithValueField } from '../../../../../components/inputs/DropdownListWithValueField';
import PulseHighlight from '../../../../../components/inputs/PulseHighlight';
import SegmentedControl from '../../../../../components/inputs/SegmentedControl';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store-hooks';
import { createFilterGroup, deleteFilterGroup, modifyFilterGroup } from '../../../../../state/slices/filter-groups-slice';
import { addNotification, NotificationType } from '../../../../../state/slices/global-slice';
import { useFormDropdownValue, useFormRadioValue, useFormStringValue, useFormUpperCaseTextAreaValue } from '../../../../home/hooks/forms';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../context/SettingsNavContext';
import FilterGroupItem from './FilterGroupItem';
import './SettingsFilterGroups.css';

enum GroupSortType {
    updateTime,
    name
}

enum FilterType {
    all,
    user,
    company
}

const SortByTypeOptions: DropdownList<GroupSortType> = [
    { value: GroupSortType.updateTime, text: 'Update Time' },
    { value: GroupSortType.name, text: 'Name' }
];

const FilterByOptions: DropdownList<FilterType> = [
    { value: FilterType.all, text: 'All Groups' },
    { value: FilterType.user, text: 'Personal Groups' },
    { value: FilterType.company, text: 'Company Groups' }
];

export const SEARCH_PARAM_FILTER_GROUP_TYPE = 'type';

export function SettingsFilterGroups() {
    useSettingsPageWidth('100%');
    const sortDropdown = useFormDropdownValue(SortByTypeOptions, GroupSortType.updateTime);
    const filterDropdown = useFormDropdownValue(FilterByOptions, FilterType.all);
    const [selectedGroupType, setSelectedGroupType] = useState<FilterGroupType | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [editingGroupId, setEditingGroupId] = React.useState<string | null>(null);
    const [recentlyUpdatedId, setRecentlyUpdatedId] = React.useState<string | null>(null);
    const filterGroups = useAppSelector((x) => x.filterGroups.groups);
    const dispatch = useAppDispatch();
    const canEditCompanyFilterGroups = useAppSelector((x) => x.user.rights.canEditCompanyFilterGroups);
    const companyFeaturesEnabled = useAppSelector((x) => x.user.rights.companyFeaturesEnabled);

    const groupTypeLabels = [
        { type: FilterGroupType.airport, label: 'Airport' },
        { type: FilterGroupType.airspace, label: 'Airspace' },
        { type: FilterGroupType.airway, label: 'Airway' },
        { type: FilterGroupType.fix, label: 'Fix' },
        { type: FilterGroupType.flightServiceStation, label: 'Flight Service Station' },
        { type: FilterGroupType.holdingPattern, label: 'Holding Pattern' },
        { type: FilterGroupType.navaid, label: 'Navaid' },
        { type: FilterGroupType.sid, label: 'SID' },
        { type: FilterGroupType.star, label: 'STAR' },
        { type: FilterGroupType.sua, label: 'SUA' },
        { type: FilterGroupType.weatherStation, label: 'Weather Station' }
    ];

    // New group dialog state
    const [newGroupDialogOpen, setNewGroupDialogOpen] = useState<boolean>(false);
    const newGroupName = useFormStringValue('');
    const newGroupMembers = useFormUpperCaseTextAreaValue('');
    const newGroupType = useFormDropdownValue<FilterGroupType>(
        groupTypeLabels.map((g) => ({ value: g.type, text: g.label })),
        selectedGroupType ?? FilterGroupType.airport
    );
    const userCompany = useFormRadioValue(
        [
            { value: FilterType.user, label: 'Personal' },
            { value: FilterType.company, label: 'Company' }
        ],
        FilterType.user
    );

    useEffect(() => {
        const typeFromUrl = Number.parseInt(searchParams.get(SEARCH_PARAM_FILTER_GROUP_TYPE) ?? '0');
        if (typeFromUrl in FilterGroupType) {
            setSelectedGroupType(typeFromUrl as FilterGroupType);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!newGroupDialogOpen) {
            newGroupName.onChange({ value: '' } as any);
            newGroupMembers.onChange({ value: '' } as any);
            userCompany.onChange({ value: FilterType.user } as any);
        }
    }, [newGroupDialogOpen]);

    useEffect(() => {
        newGroupType.onChange({ value: selectedGroupType ?? FilterGroupType.airport } as any);
    }, [selectedGroupType]);

    const handleTypeSelect = (type: FilterGroupType) => {
        setSelectedGroupType(type);
        setSearchParams(
            (searchParams) => {
                searchParams.set(SEARCH_PARAM_FILTER_GROUP_TYPE, type.toString());
                return searchParams;
            },
            { replace: true }
        );
    };

    const handleGroupUpdate = (item: FilterGroup, type: FilterGroupType) => {
        const isAlreadyFirst = displayedGroups.length > 0 && displayedGroups[0].id === item.id;

        dispatch(modifyFilterGroup({ group: item, type }));

        if (sortDropdown.value === GroupSortType.updateTime && !isAlreadyFirst) {
            setRecentlyUpdatedId(item.id);
            self.setTimeout(() => setRecentlyUpdatedId(null), 800);
        }
    };

    const handleGroupDelete = (item: FilterGroup) => {
        dispatch(deleteFilterGroup({ groupId: item.id }));
    };

    const handleGroupCreate = (item: Partial<FilterGroup>, type: FilterGroupType) => {
        dispatch(createFilterGroup({ group: item, type }));
    };

    const isNewGroupNameDuplicate = React.useMemo(() => {
        if (!newGroupName.value.trim() || selectedGroupType === null) {
            return false;
        }
        const groupsOfType = filterGroups[newGroupType.value] || [];
        return groupsOfType.some((g) => g.name.toLowerCase() === newGroupName.value.trim().toLowerCase());
    }, [newGroupName.value, selectedGroupType, newGroupType.value, filterGroups]);

    const displayedGroups = React.useMemo(() => {
        if (selectedGroupType === null) {
            return [];
        }

        let groups = filterGroups[selectedGroupType] || [];

        if (searchTerm.trim()) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            groups = groups.filter((group) => group.name.toLowerCase().includes(lowerSearchTerm));
        }

        if (filterDropdown.value === FilterType.user) {
            groups = groups.filter((group) => !group.isCompany);
        } else if (filterDropdown.value === FilterType.company) {
            groups = groups.filter((group) => group.isCompany);
        }

        groups = [...groups].sort((a, b) => {
            if (sortDropdown.value === GroupSortType.name) {
                return a.name.localeCompare(b.name);
            } else {
                const timeA = a.lastUpdate || 0;
                const timeB = b.lastUpdate || 0;
                return timeB - timeA;
            }
        });
        return groups;
    }, [filterGroups, selectedGroupType, searchTerm, filterDropdown.value, sortDropdown.value]);

    return (
        <div className="h-100 pb-2">
            <div className={'k-card settings-group-content d-flex flex-row max-h-100 h-100'}>
                <SettingsNavTitle>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>Manage filter groups</div>
                    </div>
                </SettingsNavTitle>
                <div
                    className={'d-flex flex-column'}
                    style={{ borderRight: '1px solid var(--border-color)', width: 'min(20%, 280px)', minWidth: '200px' }}>
                    <div className={'k-font-weight-medium p-2 px-3 border-bottom'}>Group types</div>

                    {groupTypeLabels.map(({ type, label }) => (
                        <div
                            className={
                                'd-flex justify-content-between align-items-center p-2 px-3 clickable ' +
                                (selectedGroupType === type ? 'selected-light-bg' : '')
                            }
                            key={type}
                            onClick={() => handleTypeSelect(type)}>
                            <div>{label}</div>
                            <div className={'fe-badge-secondary'}>{filterGroups[type]?.length ?? 0}</div>
                        </div>
                    ))}
                </div>
                <div className={'d-flex flex-column w-100 gap-2'}>
                    <div className={'d-flex justify-content-between align-items-center px-3 pt-2'}>
                        <div>
                            <div className={'k-font-weight-medium !k-font-size-lg'}>
                                {selectedGroupType !== null
                                    ? `${groupTypeLabels.find((g) => g.type === selectedGroupType)?.label} Groups`
                                    : 'Select a group type'}
                            </div>
                            <span className={'text-secondary !k-font-size-sm'}>{`${displayedGroups.length} groups`}</span>
                        </div>
                        <PulseHighlight active={selectedGroupType !== null && filterGroups[selectedGroupType].length === 0}>
                            <Button svgIcon={AddOutline} themeColor={'primary'} onClick={() => setNewGroupDialogOpen(true)}>
                                New Group
                            </Button>
                        </PulseHighlight>
                    </div>
                    <div className={'d-flex gap-2 px-3'} style={{ width: 'min(70%, 700px)' }}>
                        <TextBox
                            placeholder="Search"
                            className={'flex-grow-1'}
                            size={'small'}
                            value={searchTerm}
                            prefix={() => (
                                <div className="d-inline-flex align-items-center ps-3">
                                    <SvgIcon icon={SearchOutline} />
                                </div>
                            )}
                            onChange={(e) => setSearchTerm(e.value as string)}></TextBox>
                        {companyFeaturesEnabled && (
                            <DropdownListWithValueField style={{ maxWidth: '190px' }} {...filterDropdown}></DropdownListWithValueField>
                        )}
                        <DropdownListWithValueField style={{ maxWidth: '160px' }} {...sortDropdown}></DropdownListWithValueField>
                    </div>

                    <div className={'d-flex flex-column gap-1 px-3 overflow-y-auto flex-grow-1 border-top pt-1'}>
                        {displayedGroups.map((group, _) => (
                            <div
                                key={group.id}
                                className={recentlyUpdatedId === group.id ? 'group-item-animate-to-top' : ''}
                                style={{
                                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out',
                                    transformOrigin: 'top'
                                }}>
                                <FilterGroupItem
                                    item={group}
                                    type={selectedGroupType!}
                                    onUpdate={handleGroupUpdate}
                                    onDelete={handleGroupDelete}
                                    isEditing={editingGroupId === group.id}
                                    searchTerm={searchTerm}
                                    onEditingChange={(editing) => setEditingGroupId(editing ? group.id : null)}
                                    allGroups={filterGroups[selectedGroupType!] || []}
                                />
                            </div>
                        ))}
                        {selectedGroupType !== null && filterGroups[selectedGroupType].length === 0 && (
                            <div className="d-flex flex-column align-items-center justify-content-center h-50 text-center text-secondary">
                                <div className="k-font-size-lg k-font-weight-medium mb-2">No groups found</div>
                                <div>Create a new filter group to get started.</div>
                            </div>
                        )}
                        {selectedGroupType !== null && filterGroups[selectedGroupType].length > 0 && displayedGroups.length === 0 && (
                            <div className="d-flex flex-column align-items-center justify-content-center h-50 text-center text-secondary">
                                <div className="k-font-size-lg k-font-weight-medium mb-2">No groups match the current filters</div>
                                <div>Try adjusting your search term or filter settings.</div>
                                <div
                                    className="clickable text-primary mt-1"
                                    onClick={() => {
                                        setSearchTerm('');
                                        filterDropdown.onChange({ value: FilterType.all } as any);
                                    }}>
                                    Reset Filters
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {newGroupDialogOpen && selectedGroupType !== null && (
                    <Dialog
                        title={
                            <div>
                                <div>Create New Group</div>
                                <div className={'secondary-text-color font-400'} style={{ fontSize: '1.0rem' }}>
                                    Add a new filter group to organize your filters
                                </div>
                            </div>
                        }
                        onClose={() => setNewGroupDialogOpen(false)}
                        className={'d-flex flex-column align-items-center p-4'}>
                        <div className={'d-flex flex-column gap-3 mt-3'} style={{ minWidth: '470px' }}>
                            {companyFeaturesEnabled && (
                                <div>
                                    <SegmentedControl
                                        {...userCompany}
                                        disabled={!canEditCompanyFilterGroups}
                                        prefixes={{
                                            [FilterType.user]: <SvgIcon icon={UserOutline}></SvgIcon>,
                                            [FilterType.company]: <SvgIcon icon={UserGroupOutline}></SvgIcon>
                                        }}></SegmentedControl>
                                </div>
                            )}
                            <div className={'d-flex gap-2'}>
                                <div className={'d-flex flex-column flex-grow-1'}>
                                    <div>Group Name</div>
                                    <TextBox placeholder="e.g., NA Airports" {...newGroupName} valid={!isNewGroupNameDuplicate}></TextBox>
                                    {isNewGroupNameDuplicate && (
                                        <span className={'text-danger small mt-1'} style={{ fontSize: '.75em' }}>
                                            A group with this name already exists
                                        </span>
                                    )}
                                </div>
                                <div className={'d-flex flex-column'} style={{ minWidth: '212px' }}>
                                    <div>Type</div>
                                    <DropdownListWithValueField {...newGroupType}></DropdownListWithValueField>
                                </div>
                            </div>
                            <div className={'d-flex flex-column'}>
                                <div>Filters</div>
                                <TextArea placeholder="Enter filters separated by commas or spaces" {...newGroupMembers}></TextArea>
                            </div>
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <Button onClick={() => setNewGroupDialogOpen(false)}>Cancel</Button>
                                <Button
                                    themeColor={'primary'}
                                    disabled={isNewGroupNameDuplicate || !newGroupName.value.trim()}
                                    onClick={() => {
                                        const allMembers = newGroupMembers.value
                                            .split(/[\n,\s]+/)
                                            .map((m) => m.trim())
                                            .filter((m) => m.length > 0);

                                        const totalBeforeFilter = allMembers.length;
                                        const uniqueMembers = new Set<string>();

                                        allMembers.forEach((m) => {
                                            uniqueMembers.add(m.toUpperCase());
                                        });

                                        const duplicatesCount = totalBeforeFilter - uniqueMembers.size;

                                        if (duplicatesCount > 0) {
                                            dispatch(
                                                addNotification({
                                                    type: NotificationType.generalInfo,
                                                    customMessage: `Created group with ${duplicatesCount} duplicate filter${duplicatesCount > 1 ? 's' : ''} removed.`
                                                })
                                            );
                                        } else {
                                            dispatch(
                                                addNotification({
                                                    type: NotificationType.generalInfo,
                                                    customMessage: `Created group successfully.`
                                                })
                                            );
                                        }

                                        handleGroupCreate(
                                            {
                                                name: newGroupName.value,
                                                isCompany: userCompany.value === FilterType.company,
                                                members: Array.from(uniqueMembers).map((m) => ({ text: m }))
                                            },
                                            newGroupType.value
                                        );

                                        setNewGroupDialogOpen(false);
                                    }}>
                                    Create
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                )}
            </div>
        </div>
    );
}
